import type { ComponentPropsWithoutRef, ElementType, ForwardedRef, CSSProperties } from 'react';
import { useContext, useRef, forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { EditableTextContext } from './EditableTextRoot';
import { ClickRegion } from '../ClickRegion/ClickRegion';
import {
  getTypographicStyles,
  getDefaultTypographicElement,
} from '../../private/helpers/getTypographicStyles/getTypographicStyles';
import type { TypographicVariant } from '../../private/helpers/getTypographicStyles/getTypographicStyles';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';
import { makePolymorphic } from '../../private/helpers/makePolymorphic';
import { ellipsisStyle } from '../Ellipsis/Ellipsis';
import { lineClampCss } from '../../css/lineClampCss';

export type EditableTextDisplayProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * When true, the user can click on the display to edit the text
   */
  asTrigger?: boolean;
  renderAs?: ElementType;
};

type StyledEditableTextDisplayProps = {
  $typographicVariant: TypographicVariant;
  as?: ElementType;
  $maxLines?: number | undefined;
  $minLines?: number | undefined;
};

const StyledEditableTextDisplay = styled.div<StyledEditableTextDisplayProps>`
  ${({ $typographicVariant }) => getTypographicStyles($typographicVariant)}
  padding: var(--wui-editable-text-padding);
  border-radius: var(--wui-editable-text-border-radius);
  margin: 0;
  transition: all var(--wui-motion-duration-02) var(--wui-motion-ease);
  ${({ $maxLines }) => {
    if (isNotNil($maxLines)) {
      return css`
        ${ellipsisStyle};
        ${lineClampCss($maxLines)};
      `;
    }
    return undefined;
  }}
  ${({ $minLines }) =>
    isNotNil($minLines) &&
    css`
      min-height: calc(${$minLines}lh + calc(var(--wui-editable-text-padding) * 2));
    `}
  word-break: normal;
  overflow-wrap: anywhere;

  &[data-wui-editable-text-display='placeholder'] {
    color: var(--wui-color-text-secondary);
  }

  &:has(button) {
    user-select: none;
    cursor: pointer;

    &:hover,
    &:focus-within {
      background-color: var(--wui-color-bg-surface-hover);
    }
  }
`;

const EditableTextDisplayComponent = forwardRef<HTMLElement, EditableTextDisplayProps>(
  ({ asTrigger, renderAs, ...props }, ref) => {
    const context = useContext(EditableTextContext);
    if (!context) {
      throw new Error('EditableTextDisplay must be used within an EditableTextRoot context');
    }
    const { value, typographicVariant, setIsEditing, placeholder, maxLines, isEditing, minLines } =
      context;

    const triggerButtonRef = useRef<HTMLButtonElement>(null);

    const handleTriggerClick = () => {
      setIsEditing(true);
    };

    const elementType = renderAs ?? getDefaultTypographicElement(typographicVariant);

    const displayText = value.length > 0 ? value : placeholder;
    const isPlaceholderVisible = value.length === 0 && Boolean(placeholder);

    if (isEditing) {
      return null;
    }

    if (asTrigger && !context.readOnly) {
      return (
        <ClickRegion targetRef={triggerButtonRef}>
          <StyledEditableTextDisplay
            ref={ref as ForwardedRef<HTMLDivElement>}
            $maxLines={maxLines}
            $minLines={minLines}
            $typographicVariant={typographicVariant}
            as={elementType}
            data-wui-editable-text-display={isPlaceholderVisible ? 'placeholder' : 'value'}
            {...props}
          >
            {displayText}
            <button
              ref={triggerButtonRef}
              onClick={handleTriggerClick}
              style={{ ...visuallyHiddenStyle } as CSSProperties}
              type="button"
            >
              Edit text
            </button>
          </StyledEditableTextDisplay>
        </ClickRegion>
      );
    }

    return (
      <StyledEditableTextDisplay
        ref={ref as ForwardedRef<HTMLDivElement>}
        $maxLines={maxLines}
        $minLines={minLines}
        $typographicVariant={typographicVariant}
        as={elementType}
        data-placeholder-visible={isPlaceholderVisible || undefined}
        data-wui-editable-text-display={true}
        {...props}
      >
        {displayText}
      </StyledEditableTextDisplay>
    );
  },
);

EditableTextDisplayComponent.displayName = 'EditableTextDisplay';

export const EditableTextDisplay = makePolymorphic<'h1', EditableTextDisplayProps>(
  // @ts-expect-error makePolymorphic is causing issues with types in R19
  EditableTextDisplayComponent,
);
